import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import GlassCard from "../../../components/common/GlassCard";

function getSeverityColor(severity) {
  switch (severity?.toLowerCase()) {
    case "severe":
      return "error";
    case "moderate":
      return "warning";
    case "mild":
      return "success";
    default:
      return "default";
  }
}

function getAllergenTypeIcon(allergenType) {
  return allergenType?.toUpperCase() || "ALLERGY";
}

function AllergiesCard({ allergies, hasNKDA, onAddAllergy, onDeleteAllergy }) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activeAllergyId, setActiveAllergyId] = useState(null);

  const handleMenuOpen = (event, id) => {
    setMenuAnchor(event.currentTarget);
    setActiveAllergyId(id);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setActiveAllergyId(null);
  };

  return (
    <GlassCard
      title="Allergies & NKDA"
      icon={
        <WarningAmberIcon
          color={hasNKDA || (allergies?.length === 0) ? "success" : "warning"}
        />
      }
      onAdd={onAddAllergy}
      addLabel="Add Allergy"
      data-testid="allergies-card"
      data-card-category="allergies"
      data-nkda-status={hasNKDA ? "checked" : "not_checked"}
    >
      {hasNKDA && !allergies?.length && (
        <Box
          p={1}
          sx={{ bgcolor: "success.light", borderRadius: 1 }}
          data-testid="nkda-status-display"
          data-field-name="nkda_status"
        >
          <Typography variant="body2" color="success.dark" fontWeight={500}>
            ✓ No Known Drug Allergies (NKDA)
          </Typography>
        </Box>
      )}

      {allergies?.length ? (
        allergies.map((allergy) => (
          <Box
            key={allergy.id}
            mb={1}
            sx={{
              p: 1,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: "background.paper",
            }}
            data-testid={`allergy-item-${allergy.id}`}
            data-allergen={allergy.allergen}
            data-allergen-type={allergy.allergen_type}
            data-severity={allergy.severity}
            data-reaction={allergy.reaction}
          >
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box flex={1}>
                <Typography
                  variant="body2"
                  fontWeight={500}
                  mb={0.5}
                  data-testid={`allergy-reaction-${allergy.id}`}
                  data-field-name="reaction_type"
                >
                  {allergy.allergen || "Unknown Allergen"} • {allergy.reaction || "Not specified"}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {allergy.severity || "Unknown"} severity
                </Typography>
                {allergy.onset_date && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Since: {allergy.onset_date}
                  </Typography>
                )}
              </Box>

              <Box display="flex" gap={0.5} alignItems="center">
                <Chip
                  size="small"
                  label={getAllergenTypeIcon(allergy.allergen_type)}
                  variant="outlined"
                  data-testid={`allergy-type-chip-${allergy.id}`}
                  data-field-name="allergen_type"
                />
                <Tooltip title="Options">
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, allergy.id)}
                    data-testid={`allergy-menu-${allergy.id}`}
                    aria-label="Allergy options menu"
                    data-action="allergy-menu"
                    data-allergy-id={allergy.id}
                  >
                    <MoreVertIcon aria-label="Allergy options menu" fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        ))
      ) : !hasNKDA ? (
        <Box data-testid="no-allergies-warning">
          <Typography variant="body2" color="warning.main" fontWeight={500} mb={1}>
            ⚠️ No allergies entered
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Verify with patient or mark as NKDA if confirmed
          </Typography>
        </Box>
      ) : null}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            if (activeAllergyId) {
              onDeleteAllergy(activeAllergyId);
            }
          }}
          data-testid={`allergy-menu-delete-${activeAllergyId}`}
          data-action="delete"
          aria-label="Delete allergy"
          data-allergy-id={activeAllergyId}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </GlassCard>
  );
}

AllergiesCard.propTypes = {
  allergies: PropTypes.array,
  hasNKDA: PropTypes.bool,
  onAddAllergy: PropTypes.func.isRequired,
  onDeleteAllergy: PropTypes.func.isRequired,
};

AllergiesCard.defaultProps = {
  allergies: [],
  hasNKDA: false,
};

export default AllergiesCard;
